import React, { useState } from "react";
import { EBOARD } from "../data/images";

// ── EBOARD SECTION ────────────────────────────────────────────
// Add/edit board members in src/data/images.js → EBOARD array
// Photos work best in portrait orientation (4:5 ratio recommended)
//
// Optional per-officer fields in the EBOARD array:
//   photoZoom: 1.4               → how far to zoom in. Default is 1.4.
//                                  Raise it to crop out more background.
//   photoFocus: "50% 90%"        → the point the zoom is anchored to (x y).
//                                  A HIGHER y trims more off the top (less
//                                  wall above the head); a LOWER y trims
//                                  less off the top and more off the bottom.
//   photoPosition: "center 30%"  → only matters when a photo is taller than
//                                  the card (0% = top, 100% = bottom).

export default function EBoard() {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      id="eboard"
      style={{ padding: "80px 24px", background: "#F5F0F0" }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div className="section-header">
          <span className="section-label">Leadership</span>
          <h2 className="section-title">Meet the Executive Board</h2>
          <p className="section-subtitle">
            Your chapter leaders are here to serve, support, and guide SHPE@VT.
            Reach out anytime.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 24,
          }}
        >
          {EBOARD.map((officer, i) => (
            <div
              key={i}
              className="card"
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                overflow: "hidden",
                cursor: "default",
                transform: hovered === i ? "translateY(-6px)" : "translateY(0)",
                boxShadow:
                  hovered === i
                    ? "0 16px 40px rgba(0,0,0,0.15)"
                    : "0 2px 8px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
              }}
            >
              {/* Officer photo — UPDATE officer.photo in images.js EBOARD array */}
              <div
                style={{
                  aspectRatio: "4 / 5",
                  overflow: "hidden",
                  position: "relative",
                  background: "linear-gradient(135deg, #861F41, #5C1429)",
                }}
              >
                {officer.photo ? (
                  <img
                    src={officer.photo}
                    alt={officer.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: officer.photoPosition || "center 30%",
                      transformOrigin: officer.photoFocus || "50% 90%",
                      transition: "transform 0.4s ease",
                      transform: `scale(${
                        (officer.photoZoom || 1.4) * (hovered === i ? 1.04 : 1)
                      })`,
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  // Fallback when no photo is set
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 56,
                      color: "rgba(255,255,255,0.3)",
                    }}
                  >
                    👤
                  </div>
                )}

                {/* Hover overlay with social links */}
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to top, rgba(134,31,65,0.95), transparent)",
                    opacity: hovered === i ? 1 : 0,
                    transition: "opacity 0.3s ease",
                    display: "flex",
                    alignItems: "flex-end",
                    padding: 16,
                    gap: 10,
                  }}
                >
                  {officer.email && (
                    <a
                      href={`mailto:${officer.email}`}
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: 8,
                        padding: "7px 12px",
                        color: "white",
                        fontSize: 12,
                        fontWeight: 600,
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      ✉ Email
                    </a>
                  )}
                  {officer.linkedin && (
                    <a
                      href={officer.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: 8,
                        padding: "7px 12px",
                        color: "white",
                        fontSize: 12,
                        fontWeight: 600,
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      in LinkedIn
                    </a>
                  )}
                </div>
              </div>

              <div style={{ padding: "20px 20px 24px" }}>
                <h3
                  style={{
                    fontFamily: "Playfair Display, serif",
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#1A1A1A",
                    marginBottom: 4,
                  }}
                >
                  {officer.name}
                </h3>
                <p
                  style={{
                    fontSize: 13,
                    color: "#E5751F",
                    fontWeight: 600,
                    marginBottom: 8,
                  }}
                >
                  {officer.title}
                </p>
                {officer.major && (
                  <p style={{ fontSize: 12, color: "#888" }}>{officer.major}</p>
                )}
              </div>
            </div>
          ))}

          {/* Add a member placeholder card
          <div style={{
            border: '2px dashed #D0C0C5', borderRadius: 12,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            minHeight: 340, padding: 24, textAlign: 'center', color: '#AAA',
          }}>
            <span style={{ fontSize: 36, marginBottom: 12 }}>+</span>
            <p style={{ fontSize: 13 }}>
              Add more board members in<br />
              <code style={{ fontSize: 11, background: '#F0E8E8', padding: '2px 6px', borderRadius: 4, color: '#861F41' }}>
                src/data/images.js
              </code>
            </p>
          </div>
          */}
        </div>

        {/* CTA */}
        <div style={{ textAlign: "center", marginTop: 48 }}>
          <p style={{ color: "#888", fontSize: 15, marginBottom: 16 }}>
            Interested in running for a board position? Elections are held each
            spring.
          </p>
          <a href="mailto:shpevt@gmail.com" className="btn btn-outline">
            Contact the Board
          </a>
        </div>
      </div>
    </section>
  );
}